import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Monitor,
    Smartphone,
    Sparkles,
    PlayCircle,
    Laptop,
    X,
} from "lucide-react";
import { Mascot } from "../components/Mascot";
import { photosynthesisLesson } from "../lib/lessonData";
import { quadraticLesson } from "../lib/quadraticLessonData";
import { soundWavesLesson } from "../lib/soundWavesLessonData";
import { gaseousLesson } from "../lib/gaseousLesson";
import { angleLesson } from "../lib/angleLessonData";
import { getBackendUrl } from "../lib/utils";


/* ---------- catalog of lessons rendered on landing ---------- */
const LESSON_CATALOG = [
    {
        id: "angle-types-and-basics",
        title: angleLesson.sessionName,
        subtitle: angleLesson.subtitle,
        chapter: angleLesson.chapter,
        grade: angleLesson.grade,
        subject: "Math",
        durationMin: 20,
        slides: angleLesson.slides.length,
        sections: angleLesson.sections.length,
        accent: { ring: "#ef4444", chipBg: "#FEE2E2", chipText: "#991B1B" },
        cover:
            "https://asset.sparkl.me/pb/poc-folder/ZHH26NsPfyvHfYCl6i6hjGPPvWzNdsBQac4O5XEr.png",
        coverAlt: "Drafting compass and geometry tools",
        cta: "Start lesson",
        engine: "react",
    },
    {
        id: "photosynthesis",
        title: photosynthesisLesson.sessionName,
        subtitle: photosynthesisLesson.subtitle,
        chapter: photosynthesisLesson.chapter,
        grade: photosynthesisLesson.grade,
        subject: "Biology",
        durationMin: 25,
        slides: photosynthesisLesson.slides.length,
        sections: photosynthesisLesson.sections.length,
        accent: { ring: "#5BC2CC", chipBg: "#E0F4F4", chipText: "#2E8E9A" },
        cover:
            "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1200&q=80",
        coverAlt: "A green leaf in sunlight",
        cta: "Start lesson",
        engine: "react",
    },
    {
        id: "quadratic-equations",
        title: quadraticLesson.sessionName,
        subtitle: quadraticLesson.subtitle,
        chapter: quadraticLesson.chapter,
        grade: quadraticLesson.grade,
        subject: "Math",
        durationMin: 28,
        slides: quadraticLesson.slides.length,
        sections: quadraticLesson.sections.length,
        accent: { ring: "#7C6BF2", chipBg: "#ECE7FE", chipText: "#4A38B8" },
        cover:
            "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&w=1200&q=80",
        coverAlt: "Mathematical equations on a chalkboard",
        cta: "Start lesson",
        engine: "react",
    },
    {
        id: "gaseous-state",
        title: gaseousLesson.sessionName,
        subtitle: gaseousLesson.subtitle,
        chapter: gaseousLesson.chapter,
        grade: gaseousLesson.grade,
        subject: "Chemistry",
        durationMin: 18,
        slides: gaseousLesson.slides.length,
        sections: 5,
        accent: { ring: "#FFA987", chipBg: "#FFF1E5", chipText: "#C75730" },
        cover:
            "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=1200&q=80",
        coverAlt: "Colorful balloons being filled from a gas cylinder",
        cta: "Start lesson",
        engine: "deck",
    },
    {
        id: "characteristics-of-sound-waves",
        title: soundWavesLesson.sessionName,
        subtitle: soundWavesLesson.subtitle,
        chapter: soundWavesLesson.chapter,
        grade: soundWavesLesson.grade,
        subject: "Physics",
        durationMin: 22,
        slides: soundWavesLesson.slides.length,
        sections: soundWavesLesson.sections.length,
        accent: { ring: "#38BDF8", chipBg: "#E0F2FE", chipText: "#0369A1" },
        cover:
            "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=1200&q=80",
        coverAlt: "Stereo speaker producing visible sound vibrations",
        cta: "Start lesson",
        engine: "react",
    },
];

const SUBJECTS = ["All", "Biology", "Math", "Chemistry", "Physics"];

/**
 * Open the Student Display on a secondary monitor (if any) and navigate
 * the current window to the Teacher Panel.
 *
 * `controlMode`:
 *   - 'laptop' (default) — open student popup, then navigate this window
 *     to the Teacher Panel.
 *   - 'mobile'           — DO NOT open the Teacher Panel. The current
 *     window becomes the Student Display (fullscreen smartboard with a
 *     pairing QR). The teacher will navigate slides from the mobile app.
 */
async function startClassroomMode(sessionId, opts) {
    const lessonId = opts?.lessonId || "photosynthesis";
    const controlMode = opts?.controlMode || "laptop";
    const teacherUrl = `/teacher-panel?sessionId=${encodeURIComponent(
        sessionId,
    )}&lessonId=${encodeURIComponent(lessonId)}`;
    const studentUrl =
        opts?.studentUrl ||
        `/student-display?sessionId=${encodeURIComponent(sessionId)}`;

    // ── Mobile mode: teacher controls from phone, no teacher panel at all.
    if (controlMode === "mobile") {
        window.location.href = studentUrl;
        return;
    }

    let studentWindow = null;
    let placedOnSecondary = false;

    try {
        if ("getScreenDetails" in window) {
            try {
                opts?.setStatus?.("requesting-displays");
                const screenDetails = await window.getScreenDetails();
                const currentScreen = screenDetails.currentScreen;
                const secondaryScreen =
                    screenDetails.screens.find(
                        (screen) => screen !== currentScreen,
                    ) ||
                    screenDetails.screens.find((screen) => !screen.isPrimary);

                if (secondaryScreen) {
                    const w =
                        secondaryScreen.availWidth || secondaryScreen.width;
                    const h =
                        secondaryScreen.availHeight || secondaryScreen.height;
                    const features = [
                        `left=${secondaryScreen.left}`,
                        `top=${secondaryScreen.top}`,
                        `width=${w}`,
                        `height=${h}`,
                        "popup=yes",
                    ].join(",");

                    studentWindow = window.open(
                        studentUrl,
                        "sparkl-student-display",
                        features,
                    );

                    if (studentWindow) {
                        try {
                            studentWindow.moveTo(
                                secondaryScreen.left,
                                secondaryScreen.top,
                            );
                            studentWindow.resizeTo(w, h);
                            placedOnSecondary = true;
                        } catch {
                            /* ignore */
                        }
                    }
                }
            } catch {
                /* permission denied — fall through */
            }
        }

        if (!studentWindow) {
            studentWindow = window.open(
                studentUrl,
                "sparkl-student-display",
                "popup=yes,width=1280,height=720",
            );
        }

        if (studentWindow) {
            const nudge = () => {
                try {
                    studentWindow.postMessage(
                        { type: "sparkl:fullscreen" },
                        "*",
                    );
                } catch {
                    /* noop */
                }
            };
            setTimeout(nudge, 400);
            setTimeout(nudge, 1200);
            setTimeout(nudge, 2400);
        }

        try {
            const channel = new BroadcastChannel("sparkl-classroom-session");
            channel.postMessage({
                type: "CLASSROOM_MODE_STARTED",
                payload: {
                    sessionId,
                    studentDisplay: placedOnSecondary
                        ? "secondary"
                        : studentWindow
                          ? "opened"
                          : "failed",
                },
            });
            channel.close();
        } catch {
            /* noop */
        }

        window.location.href = teacherUrl;
    } catch {
        try {
            window.open(
                studentUrl,
                "sparkl-student-display",
                "popup=yes,width=1280,height=720",
            );
        } catch {
            /* noop */
        }
        window.location.href = teacherUrl;
    }
}

/* ---------- lesson card ---------- */
const LessonCard = ({ lesson, launching, onStart }) => {
    const isLaunching = launching === lesson.id;
    const disabled = !!launching;
    return (
        <article
            data-testid={`lesson-card-${lesson.id}`}
            className="group glass-card rounded-2xl overflow-hidden relative flex flex-col transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl"
        >
            <div
                className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-25 blur-3xl pointer-events-none"
                style={{ background: lesson.accent.ring }}
            />
            <div className="relative aspect-[16/10] overflow-hidden">
                <img
                    src={lesson.cover}
                    alt={lesson.coverAlt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div
                    className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold backdrop-blur"
                    style={{
                        background: `${lesson.accent.chipBg}EE`,
                        color: lesson.accent.chipText,
                    }}
                >
                    <Sparkles className="w-3 h-3" />
                    {lesson.subject}
                </div>
                <div className="absolute top-3 right-3 inline-flex items-center px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold bg-white/80 text-[#14202a] backdrop-blur">
                    ~{lesson.durationMin} min
                </div>
            </div>

            <div className="p-5 flex flex-col gap-2 relative">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold">
                    {lesson.grade}
                </p>
                <h3 className="text-xl font-semibold tracking-tight leading-tight">
                    {lesson.title}
                </h3>
                <p className="text-[13px] text-[#4a5662] leading-relaxed line-clamp-2">
                    {lesson.subtitle}
                </p>

                <div className="flex items-center gap-3 mt-2 text-[11px] text-[#4a5662]">
                    <span className="inline-flex items-center gap-1">
                        <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: lesson.accent.ring }}
                        />
                        {lesson.slides} slides
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-black/20" />
                        {lesson.sections} sections
                    </span>
                    {lesson.engine === "deck" && (
                        <span className="ml-auto text-[9px] uppercase tracking-[0.2em] text-[#4a5662] font-semibold px-1.5 py-0.5 rounded-full bg-black/[0.05]">
                            External deck
                        </span>
                    )}
                </div>

                <button
                    data-testid={`start-${lesson.id}-btn`}
                    onClick={() => onStart(lesson.id)}
                    disabled={disabled}
                    className="mt-4 self-start group/btn inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-semibold text-sm shadow-md active:scale-[0.98] transition-all disabled:opacity-60"
                    style={{
                        background: `linear-gradient(135deg, ${lesson.accent.ring}, ${lesson.accent.chipText})`,
                        boxShadow: `0 8px 22px ${lesson.accent.ring}55`,
                    }}
                >
                    <PlayCircle className="w-4 h-4" />
                    {isLaunching ? "Launching…" : lesson.cta}
                    <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
                </button>
            </div>
        </article>
    );
};

/* ---------- control-mode chooser modal ---------- */
const ControlModeModal = ({ lesson, onPick, onClose }) => {
    if (!lesson) return null;
    return (
        <div
            data-testid="control-mode-modal"
            className="fixed inset-0 z-50 flex items-center justify-center px-5"
        >
            {/* backdrop */}
            <button
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-[#0b1218]/60 backdrop-blur-sm"
                data-testid="control-mode-backdrop"
            />

            <div className="relative w-full max-w-xl glass-card rounded-3xl p-7 sm:p-8 shadow-[0_30px_80px_rgba(20,32,42,0.35)]">
                <button
                    aria-label="Close"
                    data-testid="control-mode-close-btn"
                    onClick={onClose}
                    className="absolute top-4 right-4 w-9 h-9 rounded-xl flex items-center justify-center text-[#14202a] hover:bg-black/[0.05] transition"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="mb-1 text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold">
                    {lesson.grade} · {lesson.subject}
                </div>
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight mb-1">
                    {lesson.title}
                </h2>
                <p className="text-[13px] text-[#4a5662] leading-relaxed mb-6">
                    How will you control this lesson today?
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                        data-testid="control-mode-laptop-btn"
                        onClick={() => onPick("laptop")}
                        className="group text-left p-5 rounded-2xl border border-black/[0.08] bg-white/80 hover:border-[#5BC2CC] hover:shadow-[0_12px_28px_rgba(91,194,204,0.18)] transition-all active:scale-[0.99]"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                                style={{
                                    background:
                                        "linear-gradient(135deg, #5BC2CC, #2E8E9A)",
                                }}
                            >
                                <Laptop className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-[#14202a]">
                                    Laptop
                                </div>
                                <div className="text-[11px] uppercase tracking-[0.18em] text-[#4a5662] font-semibold">
                                    Teacher panel
                                </div>
                            </div>
                        </div>
                        <p className="text-[12.5px] text-[#4a5662] leading-relaxed">
                            Open the teacher panel on this screen and the
                            smart-board on a second display.
                        </p>
                    </button>

                    <button
                        data-testid="control-mode-mobile-btn"
                        onClick={() => onPick("mobile")}
                        className="group text-left p-5 rounded-2xl border border-black/[0.08] bg-white/80 hover:border-[#FFA987] hover:shadow-[0_12px_28px_rgba(255,169,135,0.22)] transition-all active:scale-[0.99]"
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                                style={{
                                    background:
                                        "linear-gradient(135deg, #FFA987, #C75730)",
                                }}
                            >
                                <Smartphone className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-[#14202a]">
                                    Mobile
                                </div>
                                <div className="text-[11px] uppercase tracking-[0.18em] text-[#4a5662] font-semibold">
                                    Phone remote
                                </div>
                            </div>
                        </div>
                        <p className="text-[12.5px] text-[#4a5662] leading-relaxed">
                            Only the smart-board opens here. Pair the Sparkl
                            mobile app to navigate slides from your phone.
                        </p>
                    </button>
                </div>

                <p className="mt-5 text-[11px] text-[#4a5662] leading-relaxed">
                    You can always reach the teacher panel later from{" "}
                    <span className="font-semibold">Open the views
                    separately</span>{" "}
                    on the home screen.
                </p>
            </div>
        </div>
    );
};

/* ---------- landing page ---------- */
const Landing = () => {
    const [launching, setLaunching] = useState(null);
    const [activeSubject, setActiveSubject] = useState("All");
    const [pendingLessonId, setPendingLessonId] = useState(null);

    const visibleLessons = useMemo(() => {
        if (activeSubject === "All") return LESSON_CATALOG;
        return LESSON_CATALOG.filter((l) => l.subject === activeSubject);
    }, [activeSubject]);

    // Step 1 — clicking "Start lesson" opens the control-mode chooser.
    const openModeChooser = (lessonId) => {
        if (launching) return;
        setPendingLessonId(lessonId);
    };

    // Step 2 — user picks laptop or mobile; we then create the session.
    const startWithMode = async (controlMode) => {
        const lessonId = pendingLessonId;
        if (!lessonId) return;
        setPendingLessonId(null);
        setLaunching(lessonId);
        try {
            localStorage.removeItem("sparkl-classroom-state-v4");
        } catch {
            /* noop */
        }

        let sessionId = `${lessonId}-${Date.now()}`;
        let joinCode = null;
        let serverUrl = null;
        try {
            const base = getBackendUrl();
            if (base) {
                const r = await fetch(`${base}/axis-ai/v1/octane/sessions`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ lesson_id: lessonId }),
                });
                if (r.ok) {
                    const data = await r.json();
                    sessionId = data.session_id;
                    joinCode = data.join_code;
                    serverUrl = data.server_url;
                }
            }
        } catch {
            /* offline-friendly fallback */
        }

        if (joinCode) {
            try {
                sessionStorage.setItem(
                    `sparkl-join-code-${sessionId}`,
                    joinCode,
                );
                localStorage.setItem(
                    `sparkl-join-code-${sessionId}`,
                    joinCode,
                );
            } catch {
                /* noop */
            }
        }


        const studentUrl =
            lessonId === "gaseous-state"
                ? `/student-display?lessonId=gaseous-state&sessionId=${encodeURIComponent(
                      sessionId,
                  )}`
                : `/student-display?lessonId=${encodeURIComponent(
                      lessonId,
                  )}&sessionId=${encodeURIComponent(sessionId)}`;
        await startClassroomMode(sessionId, {
            lessonId,
            studentUrl,
            controlMode,
        });
    };

    return (
        <div className="min-h-screen ambient-bg grain flex flex-col items-center px-6 py-12">
            {/* Brand mark */}
            <div className="absolute top-8 left-10 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl sparkl-gradient flex items-center justify-center">
                    <span className="text-white font-bold text-lg">S</span>
                </div>
                <span className="font-semibold text-lg tracking-tight">
                    Sparkl<span className="text-[#5BC2CC]">.</span>
                </span>
            </div>

            {/* Hero */}
            <div className="text-center max-w-3xl mb-10 mt-8">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 border border-black/5 mb-5">
                    <span className="w-1.5 h-1.5 rounded-full sparkl-gradient" />
                    <span className="text-xs uppercase tracking-[0.2em] text-[#4a5662] font-medium">
                        AI Classroom · Lessons library
                    </span>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-4">
                    Pick a lesson and
                    <br />
                    <span className="sparkl-text-gradient">
                        let's teach it together.
                    </span>
                </h1>
                <p className="text-base text-[#4a5662] leading-relaxed max-w-xl mx-auto">
                    Each lesson opens a smart-board view on your second screen
                    and a teacher panel on this one. Filter by subject to find
                    what you want to teach today.
                </p>
            </div>

            {/* Subject filter pills */}
            <div
                data-testid="subject-filter"
                className="flex flex-wrap items-center justify-center gap-2 mb-8"
            >
                {SUBJECTS.map((s) => {
                    const isActive = s === activeSubject;
                    return (
                        <button
                            key={s}
                            data-testid={`subject-filter-${s.toLowerCase()}`}
                            onClick={() => setActiveSubject(s)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                                isActive
                                    ? "sparkl-gradient text-white border-transparent shadow-[0_6px_18px_rgba(91,194,204,0.32)]"
                                    : "bg-white/70 text-[#14202a] border-black/[0.06] hover:border-[#5BC2CC]"
                            }`}
                        >
                            {s}
                        </button>
                    );
                })}
            </div>

            {/* Lesson grid */}
            <div className="w-full max-w-6xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {visibleLessons.map((l) => (
                    <LessonCard
                        key={l.id}
                        lesson={l}
                        launching={launching}
                        onStart={openModeChooser}
                    />
                ))}
            </div>

            {visibleLessons.length === 0 && (
                <div className="text-sm text-[#4a5662] mt-6 px-6 py-4 rounded-2xl bg-white/70 border border-black/[0.05]">
                    No lessons for {activeSubject} yet. Try another subject.
                </div>
            )}

            {/* Footer — advanced */}
            <div className="mt-12 flex flex-col items-center gap-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#4a5662] font-semibold">
                    Or open the views separately
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                    <Link
                        to="/student-display"
                        target="_blank"
                        data-testid="open-student-link"
                        className="px-4 py-2 rounded-full bg-white border border-black/[0.05] hover:border-[#5BC2CC] flex items-center gap-2 text-sm text-[#14202a] transition-all"
                    >
                        <Monitor className="w-3.5 h-3.5 text-[#2E8E9A]" />
                        Student Display
                    </Link>
                    <Link
                        to="/teacher-panel"
                        target="_blank"
                        data-testid="open-teacher-link"
                        className="px-4 py-2 rounded-full bg-white border border-black/[0.05] hover:border-[#FFA987] flex items-center gap-2 text-sm text-[#14202a] transition-all"
                    >
                        <Smartphone className="w-3.5 h-3.5 text-[#C75730]" />
                        Teacher Panel
                    </Link>
                    <Link
                        to="/audio-studio"
                        data-testid="open-audio-studio-link"
                        className="px-4 py-2 rounded-full bg-white border border-black/[0.05] hover:border-[#5BC2CC] flex items-center gap-2 text-sm text-[#14202a] transition-all"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-[#5BC2CC]" />
                        Audio Studio
                    </Link>
                </div>
                <div className="mt-2 flex items-center gap-3 text-[#4a5662]">
                    <Mascot state="idle" size="xs" />
                    <p className="text-xs">
                        Tip — Chrome/Edge can place the student view on a
                        second display from one click. Pop-ups must be
                        allowed.
                    </p>
                </div>
            </div>

            {/* control-mode chooser */}
            <ControlModeModal
                lesson={
                    pendingLessonId
                        ? LESSON_CATALOG.find(
                              (l) => l.id === pendingLessonId,
                          ) || null
                        : null
                }
                onPick={startWithMode}
                onClose={() => setPendingLessonId(null)}
            />
        </div>
    );
};

export default Landing;
